import { ulid } from 'ulid';
import { WsDecodeBoardInterface, WsDecodeMetric, WsDecodeSource } from '../../../../../core/src/interfaces';
import { WsDecodeMeasurement, WsDecodeScoreView } from '../../../enums';
import { apiGet, apiPost } from '../../../helpers-sales';
import { WsDecodeCard } from './decode-data';

export interface DecodeInsightMetric {
  id: string;
  name: string;
  threshold: string;
}

export interface DecodeInsightBoardProposal {
  metrics: DecodeInsightMetric[];
}

interface DecodeInsightMetricScoringDefaults {
  readonly baselineValue: number;
  readonly staticValue: number;
  readonly measurement: WsDecodeMeasurement;
  readonly scoreView: WsDecodeScoreView;
  readonly weight: number;
}

interface DecodeInsightBoardConfiguration {
  description: string;
  metrics: WsDecodeMetric[];
}

export interface DecodeInsightBoardDraft {
  name: string;
  configuration: DecodeInsightBoardConfiguration;
}

export interface DecodeInsightResolvedMetric {
  name: string;
  source: WsDecodeSource;
}

/** @agentsNote Insights scoring defaults are fixed business settings independent of the catalogue source template; do not replace them with the generic metric factory defaults. */
const insightBoardMetricDefaults: DecodeInsightMetricScoringDefaults = {
  baselineValue: 12,
  staticValue: 10,
  measurement: WsDecodeMeasurement.Count,
  scoreView: WsDecodeScoreView.Score,
  weight: 100,
};

/** @agentsNote Fixed board proposals are keyed by insight name, not live metric values; unconfigured insights must not inherit another card's suggestions. */
const boardProposalsByInsightName: { [insightName: string]: DecodeInsightBoardProposal | undefined } = {
  'AI Usage Opportunity': {
    metrics: [
      { id: 'copilot-monthly-active-users', name: 'Copilot monthly active users', threshold: '<60% of licensed users' },
      { id: 'users-with-no-copilot-activity', name: 'Users with no Copilot activity', threshold: '>20%' },
      { id: 'average-prompts-per-user', name: 'Average prompts per user', threshold: 'Below organisational benchmark' },
      { id: 'ai-training-completion', name: 'AI training completion', threshold: '<75%' },
    ],
  },
};

export const getProposedBoardMetrics = (insightName: string): DecodeInsightMetric[] => {
  return boardProposalsByInsightName[insightName]?.metrics || [];
};

const normalizeMetricName = (name: string): string => name.trim();

const findMetricSourceByName = (sourceList: WsDecodeSource[], name: string): WsDecodeSource | undefined => {
  const normalizedName = normalizeMetricName(name);
  return sourceList.find((source) => !source.deletedAt && normalizeMetricName(source.name) === normalizedName);
};

export const ensureMetricSourceFromTemplate = async (
  sourceList: WsDecodeSource[],
  template: WsDecodeSource,
  name: string,
): Promise<WsDecodeSource | undefined> => {
  const existingSource = findMetricSourceByName(sourceList, name);

  if (existingSource) {
    return existingSource;
  }

  const newSource: WsDecodeSource = {
    id: ulid(),
    name: normalizeMetricName(name),
    dataSource: template.dataSource,
    dataTable: template.dataTable,
    dataField: template.dataField,
    dataType: template.dataType,
    classification: template.classification,
    dataValue: template.dataValue,
    dataValueList: [ ...template.dataValueList ],
  };

  const result = await apiPost('/decode/source-list', newSource);

  if (!result || !result.sourceItem || typeof result.sourceItem.id !== 'string' || !result.sourceItem.id.trim()) {
    return undefined;
  }

  const createdSource = result.sourceItem as WsDecodeSource;
  sourceList.push(createdSource);
  return createdSource;
};

export const buildInsightBoardDraft = (insightName: string, resolvedMetrics: DecodeInsightResolvedMetric[]): DecodeInsightBoardDraft => {
  const configuration: DecodeInsightBoardConfiguration = {
    description: insightName,
    metrics: resolvedMetrics.map((metric): WsDecodeMetric => ({
      ...insightBoardMetricDefaults,
      id: ulid(),
      name: normalizeMetricName(metric.name),
      sourceId: metric.source.id,
      parentId: metric.source.id,
      active: true,
      visible: true,
    })),
  };

  return { name: 'Analytics Card', configuration };
};

/** @agentsNote Only a successfully loaded catalogue without Metric Template returns null, requiring silent modal closure. Other unsuccessful API responses return undefined and must not trigger completion. */
export const createInsightBoardInInvestigations = async (
  insightName: string,
  selectedMetrics: DecodeInsightMetric[],
): Promise<WsDecodeBoardInterface | null | undefined> => {
  if (selectedMetrics.length === 0) {
    return undefined;
  }

  const sourceListResult = await apiGet('/decode/source-list');

  if (!sourceListResult || !Array.isArray(sourceListResult.sourceList)) {
    return undefined;
  }

  const sourceList: WsDecodeSource[] = [ ...sourceListResult.sourceList ];
  const template = findMetricSourceByName(sourceList, 'Metric Template');

  if (!template) {
    return null;
  }

  const boardListResult = await apiGet('/decode/boards');

  if (!boardListResult || !Array.isArray(boardListResult.decodeBoardList)) {
    return undefined;
  }

  const boardList = boardListResult.decodeBoardList as WsDecodeBoardInterface[];
  const investigationsBoard = boardList.find((board) => `${board.id}` === '101');

  if (!investigationsBoard) {
    return undefined;
  }

  const investigationsConfiguration = { ...investigationsBoard.configuration };

  if (investigationsConfiguration.profiles !== undefined && !Array.isArray(investigationsConfiguration.profiles)) {
    return undefined;
  }

  const investigationsProfiles: WsDecodeCard[] = [ ...(investigationsConfiguration.profiles || []) ];
  const resolvedMetrics: DecodeInsightResolvedMetric[] = [];

  for (const metric of selectedMetrics) {
    const source = await ensureMetricSourceFromTemplate(sourceList, template, metric.name);

    if (!source) {
      return undefined;
    }

    resolvedMetrics.push({ name: metric.name, source });
  }

  const boardDraft = buildInsightBoardDraft(insightName, resolvedMetrics);
  const createBoardResult = await apiPost('/decode/boards', { name: boardDraft.name });

  if (!createBoardResult || !createBoardResult.boardItem) {
    return undefined;
  }

  const createdBoard = createBoardResult.boardItem as WsDecodeBoardInterface;

  if (!createdBoard.id) {
    return undefined;
  }

  const createdBoardId = createdBoard.id;
  const configureBoardResult = await apiPost(`/decode/boards/${createdBoardId}`, { configuration: boardDraft.configuration });

  if (!configureBoardResult || !configureBoardResult.boardItem) {
    return undefined;
  }

  if (!investigationsProfiles.some((profile) => `${profile.boardId}` === `${createdBoardId}`)) {
    investigationsProfiles.push({ boardId: createdBoardId });
  }

  const updatedInvestigationsConfiguration = {
    ...investigationsConfiguration,
    profiles: investigationsProfiles,
    scoreView: WsDecodeScoreView.Score,
  };
  const linkBoardResult = await apiPost('/decode/boards/101', { configuration: updatedInvestigationsConfiguration });

  if (!linkBoardResult || !linkBoardResult.boardItem) {
    return undefined;
  }

  return createdBoard;
};
